import {
  Component,
  OnInit,
  OnDestroy,
  NgModule,
  Input,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { DxDrawerModule } from 'devextreme-angular/ui/drawer';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd, Event } from '@angular/router';
import { ScreenService, AppInfoService } from '../../services';
import { SideNavigationMenuModule } from 'src/app/components/library/side-navigation-menu/side-navigation-menu.component';
import { AppFooterModule } from 'src/app/components/library/app-footer/app-footer.component';
import { AppHeaderComponent } from 'src/app/components/library/app-header/app-header.component';
import { navigation } from '../../app-navigation';
import { AiAssistantComponent } from '../../components/ai-assistant/ai-assistant.component';
import { AiPanelService } from '../../services/ai-assistant/ai-panel.service';
import { PageTitleService } from '../../services/page-title.service';

import { Subscription } from 'rxjs';
import { OpenedStateMode, RevealMode } from 'devextreme/ui/drawer';

// P269 (P266 G9): routes that are not in the menu, so the header never reads "Workspace › Home" on them. A page can
// still set a more precise title through PageTitleService (the matter page shows the reference).
const ROUTE_TITLE_FALLBACKS: { prefix: string; parent: string; title: string }[] = [
  { prefix: '/crystalx/matters/', parent: 'CrystalX', title: 'Matter' },
];

@Component({
    selector: 'side-nav-outer-toolbar',
    templateUrl: './side-nav-outer-toolbar.component.html',
    styleUrls: ['./side-nav-outer-toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
      RouterModule,
      SideNavigationMenuModule,
      DxDrawerModule,
      DxButtonModule,
      CommonModule,
      AppFooterModule,
      AppHeaderComponent,
      AiAssistantComponent,
    ],
})
export class SideNavOuterToolbarComponent implements OnInit, OnDestroy {
  @ViewChild(DxScrollViewComponent, { static: true })
  scrollView!: DxScrollViewComponent;

  @Input()
  title!: string;

  selectedRoute = '';

  menuOpened!: boolean;

  temporaryMenuOpened = false;

  menuMode: OpenedStateMode = 'shrink';

  menuRevealMode: RevealMode = 'expand';

  minMenuSize = 0;

  shaderEnabled = false;

  currentSectionTitle = 'Home';

  currentSectionParent = 'Workspace';

  routerSubscription: Subscription;

  screenSubscription: Subscription;

  constructor(
    private screen: ScreenService,
    private router: Router,
    public appInfo: AppInfoService,
    public aiPanel: AiPanelService,
    public pageTitle: PageTitleService,
  ) {
    this.routerSubscription = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.selectedRoute = event.urlAfterRedirects.split('?')[0];
        this.updateCurrentSection(this.selectedRoute);
      }
    });
  }

  ngOnInit() {
    this.menuOpened = this.screen.sizes['screen-large'];

    this.screenSubscription = this.screen.changed.subscribe(() =>
      this.updateDrawer()
    );

    this.updateCurrentSection(this.router.url.split('?')[0]);
    this.updateDrawer();
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.screenSubscription.unsubscribe();
  }

  updateDrawer() {
    const isXSmall = this.screen.sizes['screen-x-small'];
    const isLarge = this.screen.sizes['screen-large'];

    this.menuMode = isLarge ? 'shrink' : 'overlap';
    this.menuRevealMode = isXSmall ? 'slide' : 'expand';
    this.minMenuSize = isXSmall ? 0 : 48;
    this.shaderEnabled = !isLarge;
  }

  get hideMenuAfterNavigation() {
    return this.menuMode === 'overlap' || this.temporaryMenuOpened;
  }

  get showMenuAfterClick() {
    return !this.menuOpened;
  }

  navigationChanged(event: DxTreeViewTypes.ItemClickEvent) {
    const path = (event.itemData as any).path;
    const pointerEvent = event.event;

    if (path && this.menuOpened) {
      if (event.node?.selected) {
        pointerEvent?.preventDefault();
      } else {
        this.router.navigate([path]);
      }

      if (this.hideMenuAfterNavigation) {
        this.temporaryMenuOpened = false;
        this.menuOpened = false;
        pointerEvent?.stopPropagation();
      }
    } else {
      pointerEvent?.preventDefault();
    }
  }

  navigationClick() {
    if (this.showMenuAfterClick) {
      this.temporaryMenuOpened = true;
      this.menuOpened = true;
    }
  }

  private updateCurrentSection(path: string) {
    const match = this.findNavigationEntry(path, navigation)
      ?? this.findNavigationEntryByPrefix(path, navigation);
    const fallback = ROUTE_TITLE_FALLBACKS.find((f) => path.startsWith(f.prefix));

    this.currentSectionTitle = match?.item.text || fallback?.title || 'Home';
    this.currentSectionParent = match?.parents[0]?.text || fallback?.parent || 'Workspace';
  }

  private findNavigationEntry(
    path: string,
    items: any[],
    parents: any[] = []
  ): { item: any; parents: any[] } | null {
    for (const item of items) {
      const itemPath =
        item.path && !/^\//.test(item.path) ? `/${item.path}` : item.path;

      if (itemPath && itemPath === path) {
        return { item, parents };
      }

      if (item.items?.length) {
        const nestedMatch = this.findNavigationEntry(path, item.items, [
          ...parents,
          item,
        ]);

        if (nestedMatch) {
          return nestedMatch;
        }
      }
    }

    return null;
  }

  private findNavigationEntryByPrefix(
    path: string,
    items: any[],
    parents: any[] = []
  ): { item: any; parents: any[] } | null {
    let bestMatch: { item: any; parents: any[] } | null = null;

    for (const item of items) {
      const itemPath =
        item.path && !/^\//.test(item.path) ? `/${item.path}` : item.path;

      if (itemPath && path.startsWith(itemPath + '/')) {
        if (!bestMatch || itemPath.length > (bestMatch.item.path?.length ?? 0)) {
          bestMatch = { item, parents };
        }
      }

      if (item.items?.length) {
        const nestedMatch = this.findNavigationEntryByPrefix(path, item.items, [
          ...parents,
          item,
        ]);
        if (nestedMatch) {
          bestMatch = nestedMatch;
        }
      }
    }

    return bestMatch;
  }
}

@NgModule({
  imports: [SideNavOuterToolbarComponent],
  exports: [SideNavOuterToolbarComponent],
})
export class SideNavOuterToolbarModule {}
